import type { ChannelSlice } from '../ChannelSlice';
import type { StickyFilterSlice } from '../StickyFilterSlice';
import type { StoreAdapter } from '../StoreAdapter';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { effect, signal, computed, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { parseKey, stringifyKey } from '@thinkalpha/table-client';
import type {
    TableClass,
    TableWidgetModel as TableWidgetContractModel,
} from '@thinkalpha/ui-types/contracts/workspace';
import { optional } from 'inversify';
import type { TableStatus } from 'src/components/table-view/model';
import { inject, injectable, ReactiveInjectable, reacts } from 'src/features/ioc';
import type { TableConstructionProgress } from 'src/models/TableModel';
import type { TableWidgetModel, TableWidgetModelEventBus } from 'src/models/TableWidgetModel';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { updateWidget } from 'src/store/actions/widgetAndChannel';
import type { ReactBindings } from 'src/types/bindings';
import { MergedEventBus, EventBusBuilder } from 'src/types/EventBus/EventBus';

@injectable()
export class TableWidgetModelImpl extends ReactiveInjectable implements TableWidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;
    #stickyFilterSlice: StickyFilterSlice;

    #tableConnectionStatus: Signal<TableStatus> = signal('uninitialized');
    #tableProgress: Signal<TableConstructionProgress> = signal(undefined);
    #tableFailureCount: Signal<number> = signal(0);
    #tableRowCount: Signal<number> = signal(0);
    #connectionStatus: ReadonlySignal<{
        status: TableStatus;
        progress: TableConstructionProgress;
        failureCount: number;
    }> = computed(() => ({
        status: this.#tableConnectionStatus.value,
        progress: this.#tableProgress.value,
        failureCount: this.#tableFailureCount.value,
    }));
    #showSettingsDialog: Signal<boolean> = signal(false);
    #refetchCallback: (() => void) | null = null;
    #tableKey: Signal<string | null> = signal(null);
    #tableClass: Signal<TableClass | null> = signal(null);
    #tableKeys: Signal<readonly string[]> = signal([]);

    headerElements: WidgetHeaderElement[] = [
        ...COMMON_HEADER_ELEMENTS,
        { type: 'stickyFilter', position: 40, priority: 5 },
        { type: 'tableRowCount', position: 110, priority: 20 },
        { type: 'connectionStatus', position: 111, priority: 21 },
        { type: 'tableWidgetSettings', position: 112, priority: 22 },
    ];

    eventBus!: TableWidgetModelEventBus;

    constructor(
        @inject('WidgetDataModel') @reacts private widgetData: ReactBindings['WidgetDataModel'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('StickyFilterSlice') stickyFilterSlice: StickyFilterSlice,
        @inject('ProxyClientService') private proxyClientService: ReactBindings['ProxyClientService'],
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#stickyFilterSlice = stickyFilterSlice;

        // The settings UI needs keys, not ownership of the raw proxy client. Follow the target signal
        // here and keep RxJS confined to the underlying key stream.
        let keysSubscription: { unsubscribe(): void } | undefined;
        this.disposableStack.defer(
            effect(() => {
                const client = this.proxyClientService.target.value.client;
                untracked(() => {
                    keysSubscription?.unsubscribe();
                    this.#tableKeys.value = [];
                    keysSubscription = client.keys$.subscribe((keys) => {
                        this.#tableKeys.value = keys;
                    });
                });
            }),
        );
        this.disposableStack.defer(() => keysSubscription?.unsubscribe());

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        this.eventBus = this.#buildEventBus();
    }

    init(tabId: string): void {
        this.widgetData.init(tabId);

        // Initialize slices for new widget header
        this.#channelSlice.init(tabId);
        this.#widgetTabSlice.init(tabId);
        // Lightweight adapter for StickyFilterSlice
        const stickyFilterAdapter: StoreAdapter<{ filter?: string | null }> = {
            getState: () => ({
                filter: (this.widgetData.widget as TableWidgetContractModel).filter ?? null,
            }),
            persist: (partial) => {
                if ('filter' in partial) {
                    this.store.dispatch(updateWidget({ tabId, widgetUpdates: { filter: partial.filter } }));
                }
            },
            subscribe: (cb) => {
                return this.store
                    .subscribeSelector((state) => {
                        const tab = state.tab.byId[tabId];
                        if (!tab) return null;
                        return { filter: (tab.widget as TableWidgetContractModel).filter ?? null };
                    })
                    .handle((data) => {
                        if (data) cb(data);
                    });
            },
            [Symbol.dispose]() {
                // no-op — lifecycle managed by disposableStack
            },
        };
        this.#stickyFilterSlice.init(stickyFilterAdapter);
        this.disposableStack.use(this.#channelSlice);
        this.disposableStack.use(this.#widgetTabSlice);
        this.disposableStack.use(this.#stickyFilterSlice);

        const widget = this.widgetData.widget as TableWidgetContractModel;
        this.#tableKey.value = widget.table ? stringifyKey(widget.table) : null;
        this.#tableClass.value = widget.class ?? null;

        const unsubscribe = this.store
            .subscribeSelector((state) => {
                const tab = state.tab.byId[tabId];
                if (!tab) return null;
                const widget = tab.widget as TableWidgetContractModel;
                return {
                    tableKey: widget.table ? stringifyKey(widget.table) : null,
                    tableClass: widget.class ?? null,
                };
            })
            .handle((widgetState) => {
                if (widgetState) {
                    this.#tableKey.value = widgetState.tableKey;
                    this.#tableClass.value = widgetState.tableClass;
                }
            });
        this.disposableStack.defer(unsubscribe);
    }

    #buildEventBus(): TableWidgetModelEventBus {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Events
                .addEvent('widget::onTableKeySelect', ({ value }) =>
                    this.store.dispatch(
                        updateWidget({
                            tabId: this.widgetData.tabId,
                            widgetUpdates: {
                                table: parseKey(value),
                            },
                        }),
                    ),
                )
                .addEvent('widget::onTableClassSelect', ({ tableClass }) =>
                    this.store.dispatch(
                        updateWidget({
                            tabId: this.widgetData.tabId,
                            widgetUpdates: {
                                class: tableClass,
                            },
                        }),
                    ),
                )
                .addEvent('widget::setShowTableSettingsDialog', ({ show }) => (this.#showSettingsDialog.value = show))
                // Signals
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addSignal('widget::tableRowCount', () => this.#tableRowCount)
                .addSignal('widget::selectedTableKey', () => this.#tableKey)
                .addSignal('widget::showTableSettingsDialog', () => this.#showSettingsDialog)
                .addSignal('widget::tableKeys', () => this.#tableKeys)
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.#refetchCallback?.();
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus)
            .merge(this.#stickyFilterSlice.eventBus);
    }

    /**
     * Update the table connection status (called from the component).
     */
    setTableConnectionStatus(status: TableStatus): void {
        this.#tableConnectionStatus.value = status;
    }

    /**
     * Update the table row count (called from the component).
     */
    setTableRowCount(count: number): void {
        this.#tableRowCount.value = count;
    }

    setRefetchCallback(fn: (() => void) | null): void {
        this.#refetchCallback = fn;
    }
}
